import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Anchor, Button, Stack, Text, Title } from "@mantine/core";
import { authErrorMessage, useAuth } from "@/auth";
import { ToastHost, useToast } from "@/utility/toast-utility";

/** Shown after sign-up (and on sign-in) until the email address is verified. */
export default function VerifyEmail() {
  const { user, emailVerified, resendVerification, refresh, signOut } = useAuth();
  const navigate = useNavigate();
  const { closeToast, showToast, toast } = useToast();
  const [busy, setBusy] = useState(false);

  if (!user) return <Navigate to="/auth/login" replace />;
  if (emailVerified) return <Navigate to="/app/home" replace />;

  const run = async (fn: () => Promise<void>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) showToast({ type: "success", message: ok });
    } catch (err) {
      showToast({ type: "error", message: authErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack align="center" gap="md" mt="md" ta="center">
      <ToastHost toast={toast} onClose={closeToast} />
      <Title order={3}>Check your email</Title>
      <Text maw={480}>
        We sent a verification link to <Text span fw={600}>{user.email}</Text>. Open it, then come back here.
      </Text>
      <Button size="md" loading={busy} onClick={() => run(async () => { await refresh(); navigate("/auth/verify-email", { replace: true }); })}>
        I've verified my email
      </Button>
      <Anchor onClick={() => run(resendVerification, "Verification email sent again")}>Resend the email</Anchor>
      <Anchor c="dimmed" onClick={() => run(async () => { await signOut(); navigate("/auth/login"); })}>
        Use a different account
      </Anchor>
    </Stack>
  );
}
